import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastChakra } from "../../helpers/toast";
import {
  Box, HStack, Image, IconButton, Stack, Table, Tbody,
  Td, Th, Thead, Tr, Text, Tooltip, VStack, Flex, Grid
} from "@chakra-ui/react";
import { FaArrowLeft } from "react-icons/fa";
import logoIE from "../../assets/img/logo_nery.jpg";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { BsFillCloudDownloadFill, BsFillImageFill } from "react-icons/bs";
import { getPresupuestoById } from "../../features/presupuestoSlice";
import { getConfiguraciones } from "../../features/configuracionesSlice";

const PresupuestoPdf = () => {
  const componentRef = useRef();
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const [presupuesto, setPresupuesto] = useState(location.state?.presupuesto || null);
  const { isLoading, presupuesto: presupuestoFromStore, isError, message } = useSelector((state) => state.presupuestos);
  const { configuracionesList } = useSelector((state) => state.configuraciones);
  const [loadingImage, setLoadingImage] = useState(false);

  const [loadingDowloadPdf, setloadingDowloadPdf] = useState(false);

  useEffect(() => {
    if (!presupuesto) {
      dispatch(getPresupuestoById(id));
      dispatch(getConfiguraciones());
    }
  }, [presupuesto, id, dispatch]);

  useEffect(() => {
    if (presupuestoFromStore) {
      setPresupuesto(presupuestoFromStore);
    }
  }, [presupuestoFromStore]);

  useEffect(() => {
    if (isError) ToastChakra("Error", message, "error", 1500);
  }, [isError, message]);

  const handleDownloadPdf = () => {
    const element = componentRef.current;
  
    setloadingDowloadPdf(true);
  
    const waitForImages = () => {
      const images = element.querySelectorAll("img");
      return Promise.all(
        Array.from(images).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = img.onerror = resolve;
          });
        })
      );
    };
  
    const waitForFonts = () => {
      if (document.fonts && document.fonts.ready) {
        return document.fonts.ready;
      }
      return Promise.resolve();
    };
  
    Promise.all([waitForImages(), waitForFonts()])
      .then(() => {
        return html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#fff",
          scrollY: 0, // Muy importante si hay scrolls
          windowWidth: document.documentElement.offsetWidth,
          windowHeight: document.documentElement.offsetHeight,
        });
      })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "pt",
          format: "a4",
        });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
  
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`presupuesto_${presupuesto?.numero}.pdf`);
        setloadingDowloadPdf(false);
      })
      .catch((error) => {
        console.error("Error generando PDF:", error);
        setloadingDowloadPdf(false);
      });
  };

  const handleDownloadImage = () => {
    setLoadingImage(true);
    html2canvas(componentRef.current, { scale: 6, useCORS: true, backgroundColor: "#fff" })
      .then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = imgData;
        link.download = `Presupuesto_nro_${presupuesto?.numero}.png`;
        link.click();
        setLoadingImage(false);
      });
  };

  const totalBase = presupuesto?.detalles.reduce((sum, item) => sum + Number(item.precio_unitario), 0).toFixed(2);
  const totalIVA = presupuesto?.detalles.reduce((sum, item) => sum + (item.precio_total - item.precio_unitario), 0).toFixed(2);
  const totalDto = presupuesto?.detalles.reduce((sum, item) => sum + (item.precio_unitario * ((item.descuento || 0)/100)), 0).toFixed(2);

  if (isLoading) return <Text>Cargando...</Text>;

  return (
    <>
      <Stack spacing={4} direction="row" justifyContent="space-between" py={4}>
        <HStack spacing={4} direction="row">
          <Link to="/presupuesto">
            <IconButton icon={<FaArrowLeft />} colorScheme="gray" rounded="full" />
          </Link>
          <Text fontSize="md" fontWeight="bold">Regresar</Text>
        </HStack>
        <HStack spacing={2} direction="row">
          <Tooltip label="Descargar en PDF">
            <IconButton isLoading={loadingDowloadPdf} colorScheme="green" onClick={handleDownloadPdf} icon={<BsFillCloudDownloadFill />} />
          </Tooltip>
          <Tooltip label="Descargar en Imagen">
            <IconButton isLoading={loadingImage} colorScheme="blue" onClick={handleDownloadImage} icon={<BsFillImageFill />} />
          </Tooltip>
        </HStack>
      </Stack>

      <Box ref={componentRef} 
        bg="white"
        w="210mm"
        minH="297mm"
        color="black"
        p={6}
        mx="auto"
        fontFamily="Arial, sans-serif"
        fontSize="14px"
      >
      

        <HStack spacing={4} align="stretch" mb={2}>
          <Box border="1px solid black" p={4} w="50%">
          <VStack spacing={1} textAlign="center">
               <Image
                  src={configuracionesList.logo || logoIE} // fallback si no viene la imagen
                  alt="logo"
                  boxSize="60px"
                  objectFit="contain"
              />
              <Text fontWeight="bold" fontStyle="italic">
              {configuracionesList.nombre}
              </Text>
            </VStack>
          </Box>
          <Box border="1px solid black" p={4} w="50%">
              <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={4}>PRESUPUESTO</Text>
              <Text><strong>Fecha:</strong> {moment(presupuesto?.fechaPresupuesto).format("DD/MM/YYYY")}</Text>
              <Text><strong>Precio Total:</strong> {parseFloat(presupuesto?.totalGeneral).toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: true,
                          })} €</Text>
              <Text><strong>Nro. de Presupuesto:</strong> {presupuesto?.numero}</Text>
          </Box>
        </HStack>

        <HStack spacing={4} align="stretch" mb={2}>
          {/* EMPRESA */}
          <Box border="1px solid black" p={4} w="50%">
            <Text fontWeight="bold" mb={2}>EMPRESA</Text>
            <Grid templateColumns="120px 1fr" gap={1} fontSize="sm">
              <Text fontWeight="bold">Nombre:</Text>
              <Text>{configuracionesList.nombre}</Text>
              <Text fontWeight="bold">Dirección:</Text>
              <Text>{configuracionesList.direccion}</Text>
              <Text fontWeight="bold">NIF/CIF:</Text>
              <Text>{configuracionesList.dni}</Text>
              <Text fontWeight="bold">Teléfono:</Text>
              <Text>{configuracionesList.telefono}</Text>
              <Text fontWeight="bold">Email:</Text>
              <Text>{configuracionesList.email}</Text>
            </Grid>
          </Box>

          {/* CLIENTE */}
          <Box border="1px solid black" p={4} w="50%">
            <Text fontWeight="bold" mb={2}>CLIENTE</Text>
            <Grid templateColumns="120px 1fr" gap={1} fontSize="sm">
              <Text fontWeight="bold">Nombre:</Text>
              <Text>{presupuesto?.cliente}</Text>
              <Text fontWeight="bold">Dirección:</Text>
              <Text>{presupuesto?.direccion}</Text>
              <Text fontWeight="bold">NIF/CIF:</Text>
              <Text>{presupuesto?.dni}</Text>
              <Text fontWeight="bold">Teléfono:</Text>
              <Text>{presupuesto?.celular}</Text>
              <Text fontWeight="bold">Email:</Text>
              <Text>{presupuesto?.email}</Text>
            </Grid>
          </Box>
        </HStack>


        <Box mb={4}>
          <Text fontWeight="bold" textDecoration="underline">DESCRIPCIÓN DE LA REFORMA Y PRESUPUESTO:</Text>
          <Text>{presupuesto?.descripcion_reforma}</Text>
        </Box>

        {/* CABECERA DE LA TABLA */}
             <Box border="2px solid black" borderRadius="md" p={4} w="100%">
               <Table width="100%" size="sm" __css={{ borderCollapse: 'collapse' }}>
                     <Th
                       w="45%" // Coincide con Td
                       fontSize="sm"
                       fontWeight="bold"
                       color="black"
                       textAlign="left"
                       border="none"
                     >
                       Descripción
                     </Th>
                     <Th
                       w="8%" // Coincide con Td
                       fontSize="sm"
                       fontWeight="bold"
                       color="black"
                       isNumeric
                       border="none"
                     >
                       Cant.
                     </Th>
                     <Th
                       w="18%" // Coincide con Td
                       fontSize="sm"
                       fontWeight="bold"
                       color="black"
                       isNumeric
                       border="none"
                     >
                       P. Unitario
                     </Th>
                     <Th
                       w="3%" // Coincide con Td
                       fontSize="sm"
                       fontWeight="bold"
                       color="black"
                       isNumeric
                       border="none"
                     >
                       IVA
                     </Th>
                     <Th
                       w="26%" // Coincide con Td
                       fontSize="sm"
                       fontWeight="bold"
                       color="black"
                       isNumeric
                       border="none"
                       textAlign="right"
                     >
                       Precio Total
                     </Th>
               </Table>
             </Box>
        {/* CUERPO DE LA TABLA */}
         <HStack spacing={6} align="stretch" mt={1}> {/* Aquí es donde agregaremos el espacio */}
            <Box
              borderRadius="md"
              height="390px"
              width="100%"
              border="2px solid black"
              overflow="hidden"
            >
                <Table width="100%" tableLayout="fixed" size="sm" __css={{ borderCollapse: 'collapse' }}>
                  <Tbody>
                    {presupuesto?.detalles.map((item, index) => (
                      <Tr key={index}>
                        <Td
                          w="45%" // Ajustado para coincidir con Th
                          fontSize="xs"
                          borderTop="none"
                          borderBottom="none"
                          borderLeft="1px solid black"
                          borderRight="1px solid black"
                        >
                          {item.descripcion}
                        </Td>
                        <Td
                          w="8%" // Ajustado para coincidir con Th (era 3%)
                          fontSize="xs"
                          isNumeric
                          borderTop="none"
                          borderBottom="none"
                          borderLeft="1px solid black"
                          borderRight="1px solid black"
                        >
                          {item.cantidad}
                        </Td>
                        <Td
                          w="18%" // Ajustado para coincidir con Th (era 15%)
                          fontSize="xs"
                          isNumeric
                          borderTop="none"
                          borderBottom="none"
                          borderLeft="1px solid black"
                          borderRight="1px solid black"
                        >
                          {parseFloat(item.precio_unitario).toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: true,
                          })}
                        </Td>
                        <Td
                          w="3%" // Ajustado para coincidir con Th
                          fontSize="xs"
                          isNumeric
                          borderTop="none"
                          borderBottom="none"
                          borderLeft="1px solid black"
                          borderRight="1px solid black"
                        >
                          {item.iva}%
                        </Td>
                        <Td
                          w="26%" // Ajustado para coincidir con Th (era 30%)
                          fontSize="xs"
                          isNumeric
                          borderTop="none"
                          borderBottom="none"
                          borderLeft="1px solid black"
                          borderRight="1px solid black"
                        >
                          {parseFloat(item.precio_total).toLocaleString('es-ES', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: true,
                          })}
                        </Td>
                      </Tr>
                    ))}
    
                    {/* Rellenar con filas vacías */}
                    {Array.from({ length: 20 - (presupuesto?.detalles.length || 0) }).map((_, idx) => (
                      <Tr key={`empty-${idx}`} height="34px">
                        <Td w="45%" borderTop="none" borderBottom="none" borderLeft="1px solid black" borderRight="1px solid black">&nbsp;</Td>
                        <Td w="8%" borderTop="none" borderBottom="none" borderLeft="1px solid black" borderRight="1px solid black">&nbsp;</Td>
                        <Td w="18%" borderTop="none" borderBottom="none" borderLeft="1px solid black" borderRight="1px solid black">&nbsp;</Td>
                        <Td w="3%" borderTop="none" borderBottom="none" borderLeft="1px solid black" borderRight="1px solid black">&nbsp;</Td>
                        <Td w="26%" borderTop="none" borderBottom="none" borderLeft="1px solid black" borderRight="1px solid black">&nbsp;</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
          </HStack>
          
        <Flex justify="space-between" mt={6} align="flex-start">
            {/* Firma del Cliente */}
            <Box textAlign="center">
              <Text fontWeight="bold" mb={2}>FIRMA DEL CLIENTE</Text>
              <Box borderBottom="1px solid black" w="200px" h="1px" mx="auto" mt={12} />
              <Text mt={2} fontSize="sm" fontWeight="medium">Firma y Sello</Text>
            </Box>

            {/* Totales */}
            <Stack spacing={2} align="flex-end">
              <Text><strong>Total Base:</strong> {parseFloat(totalBase).toLocaleString('es-ES', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: true
                  })} €</Text>
              <Text><strong>Total IVA:</strong> {parseFloat(totalIVA).toLocaleString('es-ES', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: true
                  })} €</Text>
              <Text><strong>Total Gral:</strong> {parseFloat(presupuesto?.totalGeneral).toLocaleString('es-ES', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: true
                  })} €</Text>
            </Stack>
        </Flex>


        <Box mt={6}>
            <Text><strong>Observación: </strong>{presupuesto?.observaciones}</Text>
        </Box>
        <Box mb={6} mt={2} textAlign="center">
          <Text fontSize="xs">La información de este presupuesto no es una factura y solo es una estimación de los servicios descritos más arriba</Text>
        </Box>
      </Box>
    </>
  );
};

export default PresupuestoPdf;